import { peekCurrentOrganizationTimezoneCache } from '@/session/organizationTimezoneCache';

export type DateInput = string | number | Date;

export interface TimezoneFormatOptions {
  locale?: string;
  formatOptions?: Intl.DateTimeFormatOptions;
  includeTimeZoneName?: boolean | 'short' | 'long';
}

export interface FormattedZonedDate {
  text: string;
  timezone: string;
  abbreviation: string;
  isOrgTimezone: boolean;
}

const DEFAULT_LOCALE = 'en-US';

const DEFAULT_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  hourCycle: 'h12',
};

export const getBrowserTimezone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone;

export const resolveActiveTimezone = (organization: string | null = null, subject: string | null = null): string =>
  peekCurrentOrganizationTimezoneCache(organization, subject) ?? getBrowserTimezone();

const toDate = (input: DateInput): Date => {
  if (input instanceof Date) {
    return input;
  }
  const parsed = new Date(input);
  if (Number.isNaN(parsed.getTime())) {
    throw new Error('Invalid date input provided to timezone formatter.');
  }
  return parsed;
};

const getTimezoneAbbreviation = (date: Date, locale: string, timezone: string): string => {
  const parts = new Intl.DateTimeFormat(locale, {
    timeZone: timezone,
    timeZoneName: 'short',
  }).formatToParts(date);
  return parts.find((part) => part.type === 'timeZoneName')?.value || '';
};

export const formatWithTimezone = (
  input: DateInput,
  timezone: string,
  isOrgTimezone: boolean,
  options?: TimezoneFormatOptions
): FormattedZonedDate => {
  const date = toDate(input);
  const locale = options?.locale ?? DEFAULT_LOCALE;
  const baseOptions: Intl.DateTimeFormatOptions = {
    ...DEFAULT_FORMAT_OPTIONS,
    ...(options?.includeTimeZoneName
      ? {
          timeZoneName: options.includeTimeZoneName === true ? 'short' : options.includeTimeZoneName,
        }
      : {}),
    ...options?.formatOptions,
    timeZone: options?.formatOptions?.timeZone ?? timezone,
  };

  const formatter = new Intl.DateTimeFormat(locale, baseOptions);
  const text = formatter.format(date);
  const abbreviation =
    baseOptions.timeZoneName === 'short' || options?.includeTimeZoneName
      ? getTimezoneAbbreviation(date, locale, baseOptions.timeZone || timezone)
      : getTimezoneAbbreviation(date, locale, baseOptions.timeZone || timezone);

  return {
    text,
    timezone: baseOptions.timeZone || timezone,
    abbreviation,
    isOrgTimezone,
  };
};
